import { memo } from 'react'
import { Node, Edge } from 'reactflow'
import { Button } from "@/shared/components/ui/button"
import { Input } from "@/shared/components/ui/input"
import { Label } from "@/shared/components/ui/label"
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from "@/shared/components/ui/select"
import { Switch } from "@/shared/components/ui/switch"
import { ChevronUp, ChevronDown } from "lucide-react"

interface BinderNodeConfigProps {
  selectedNode: Node
  nodes: Node[]
  edges: Edge[]
  onUpdateNodeData: (field: string, value: unknown) => void
  onMoveEdgeOrder: (sourceNodeId: string, currentIndex: number, direction: 'up' | 'down') => void
}

export const BinderNodeConfig = memo(({
  selectedNode,
  nodes,
  edges,
  onUpdateNodeData,
  onMoveEdgeOrder
}: BinderNodeConfigProps) => {
  
  return (
    <>
      <div className="space-y-2">
        <Label>节点名称</Label>
        <Input 
          value={selectedNode.data.label || ''} 
          onChange={(e) => onUpdateNodeData('label', e.target.value)}
          placeholder="并行编排器"
        />
      </div>

      <div className="space-y-2">
        <Label>超时时间（秒）</Label>
        <Input 
          type="number"
          value={selectedNode.data.timeout || 30} 
          onChange={(e) => onUpdateNodeData('timeout', parseInt(e.target.value) || 30)}
          min={1}
          max={300}
        />
        <p className="text-xs text-slate-500">
          等待所有分支完成的最大时间
        </p>
      </div>

      <div className="space-y-2">
        <Label>执行策略</Label>
        <Select 
          value={selectedNode.data.strategy || 'parallel'} 
          onValueChange={(value) => onUpdateNodeData('strategy', value)}
        >
          <SelectTrigger>
            <SelectValue placeholder="选择执行策略" />
          </SelectTrigger>
          <SelectContent>
            <SelectItem value="parallel">并行执行</SelectItem>
            <SelectItem value="sequential">顺序执行</SelectItem>
          </SelectContent>
        </Select>
        <p className="text-xs text-slate-500">
          {selectedNode.data.strategy === 'sequential' ? '按创建顺序依次执行分支' : '所有分支同时启动（推荐）'}
        </p>
      </div>

      <div className="space-y-2">
        <Label>等待策略</Label>
        <Select 
          value={selectedNode.data.wait_for || 'all'} 
          onValueChange={(value) => onUpdateNodeData('wait_for', value)}
        >
          <SelectTrigger>
            <SelectValue placeholder="选择等待策略" />
          </SelectTrigger>
          <SelectContent>
            <SelectItem value="all">等待所有分支</SelectItem>
            <SelectItem value="any">任意一个完成</SelectItem>
            <SelectItem value="primary">仅等待主分支</SelectItem>
          </SelectContent>
        </Select>
        <p className="text-xs text-slate-500">
          {selectedNode.data.wait_for === 'any' ? '第一个完成的分支返回' : 
           selectedNode.data.wait_for === 'primary' ? '只等待主分支完成' : 
           '等待所有分支完成（默认）'}
        </p>
      </div>

      <div className="flex items-center justify-between">
        <div className="space-y-1">
          <Label htmlFor="output-mode-switch" className="text-sm">顺序输出</Label>
          <p className="text-xs text-slate-500 max-w-[200px]">
            按分支顺序依次展示输出，上一个完成后再显示下一个
          </p>
        </div>
        <Switch
          id="output-mode-switch"
          checked={selectedNode.data.output_mode === 'sequential'}
          onCheckedChange={(checked) =>
            onUpdateNodeData('output_mode', checked ? 'sequential' : undefined)
          }
        />
      </div>

      {/* 分支配置说明 */}
      <div className="mt-4 p-3 bg-cyan-50 rounded-lg border border-cyan-200">
        <h4 className="text-sm font-medium text-cyan-800 mb-2">💡 分支配置</h4>
        <ul className="text-xs text-cyan-700 space-y-1">
          <li>• 从此节点拖出连线到其他节点创建分支</li>
          <li>• 点击连线可配置分支的静默模式和主分支设置</li>
          <li>• 主分支：输出保存到对话记忆</li>
          <li>• 静默分支：不向用户输出内容</li>
        </ul>
      </div>

      {/* Sequential 模式的分支顺序编辑 */}
      {selectedNode.data.strategy === 'sequential' && 
       edges.filter(edge => edge.source === selectedNode.id).length > 1 && (
        <div className="mt-4 p-3 bg-amber-50 rounded-lg border border-amber-200">
          <h4 className="text-sm font-medium text-amber-800 mb-2">🔄 执行顺序</h4>
          <div className="space-y-2">
            {edges
              .filter(edge => edge.source === selectedNode.id)
              .sort((a, b) => (a.data?.order ?? 0) - (b.data?.order ?? 0))
              .map((edge, index) => {
                const targetNode = nodes.find((n: Node) => n.id === edge.target)
                return (
                  <div key={edge.id} className="flex items-center justify-between bg-white rounded px-2 py-1">
                    <span className="text-xs text-slate-600">
                      {index + 1}. {targetNode?.data?.label || targetNode?.data?.name || `节点 ${edge.target}`}
                    </span>
                    <div className="flex gap-1">
                      <Button
                        size="sm"
                        variant="ghost"
                        className="h-6 w-6 p-0"
                        onClick={() => onMoveEdgeOrder(selectedNode.id, index, 'up')}
                        disabled={index === 0}
                      >
                        <ChevronUp className="h-3 w-3" />
                      </Button>
                      <Button
                        size="sm"
                        variant="ghost"
                        className="h-6 w-6 p-0"
                        onClick={() => onMoveEdgeOrder(selectedNode.id, index, 'down')}
                        disabled={index === edges.filter(e => e.source === selectedNode.id).length - 1}
                      >
                        <ChevronDown className="h-3 w-3" />
                      </Button>
                    </div>
                  </div>
                )
              })}
          </div>
          <p className="text-xs text-amber-600 mt-2">
            分支将按此顺序依次执行
          </p>
        </div>
      )}

      {/* 当前分支状态显示 */}
      {(() => {
        const outgoingEdges = edges.filter(edge => edge.source === selectedNode.id)
        const branchCount = outgoingEdges.length
        const primaryBranches = outgoingEdges.filter(edge => edge.data?.is_primary).length
        const silentBranches = outgoingEdges.filter(edge => edge.data?.silent).length

        if (branchCount === 0) {
          return (
            <div className="mt-4 p-3 bg-slate-50 rounded-lg text-center">
              <span className="text-2xl">🔗</span>
              <p className="text-sm text-slate-600 mt-1">暂无分支</p>
              <p className="text-xs text-slate-400">拖出连线创建分支</p>
            </div>
          )
        }

        return (
          <div className="mt-4 p-3 bg-slate-50 rounded-lg">
            <h4 className="text-sm font-medium text-slate-700 mb-2">当前分支状态</h4>
            <div className="flex flex-wrap gap-2">
              <span className="bg-blue-100 text-blue-700 px-2 py-1 rounded text-xs">
                {branchCount} 个分支
              </span>
              {primaryBranches > 0 && (
                <span className="bg-green-100 text-green-700 px-2 py-1 rounded text-xs">
                  {primaryBranches} 主分支
                </span>
              )}
              {silentBranches > 0 && (
                <span className="bg-gray-100 text-gray-700 px-2 py-1 rounded text-xs">
                  {silentBranches} 静默
                </span>
              )}
            </div>
            {primaryBranches === 0 && branchCount > 0 && (
              <p className="text-xs text-amber-600 mt-2">
                ⚠️ 建议设置至少一个主分支用于保存对话记忆
              </p>
            )}
          </div>
        )
      })()}
    </>
  )
})

BinderNodeConfig.displayName = 'BinderNodeConfig'